import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield } from 'lucide-react';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen hero-bg px-6 py-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <button
          onClick={() => navigate('/')}
          className="flex items-center text-gray-600 hover:text-rose-600 transition-colors mb-8"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Home
        </button>

        <div className="glass-card p-8">
          <div className="flex items-center justify-center mb-6">
            <Shield className="w-12 h-12 text-rose-600 mr-3" />
            <h1 className="text-3xl md:text-4xl font-bold gradient-text">Privacy Policy</h1>
          </div>

          <p className="text-gray-600 text-center mb-8">
            Last Updated: January 2025
          </p>

          <div className="space-y-8 text-gray-700">
            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">1. Information We Collect</h2>
              <p className="mb-3">
                We collect information you provide directly to us when you:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Create an account (name, email, password)</li>
                <li>Complete relationship assessments (quiz responses)</li>
                <li>Provide optional demographic information (age, gender, relationship status)</li>
                <li>Use our AI chat assistant (conversation data)</li>
                <li>Invite partners to couples mode (partner's email)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">2. How We Use Your Information</h2>
              <p className="mb-3">We use the information we collect to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Provide personalized relationship insights using AI</li>
                <li>Generate customized PDF reports based on your responses</li>
                <li>Enable couples mode features and compatibility analysis</li>
                <li>Send important account notifications and verification emails</li>
                <li>Improve our services and develop new features</li>
                <li>Ensure security and prevent fraud</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">3. Data Security</h2>
              <p>
                We implement industry-standard security measures to protect your personal information:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>Passwords are encrypted using bcrypt hashing</li>
                <li>Data transmission is secured via HTTPS/SSL</li>
                <li>Access to personal data is restricted to authorized personnel only</li>
                <li>Regular security audits and updates</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">4. Data Sharing</h2>
              <p className="mb-3">
                We do NOT sell your personal information to third parties. We may share data with:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>AI Service Providers:</strong> OpenAI for generating personalized insights (data is processed securely)</li>
                <li><strong>Email Services:</strong> To send verification and notification emails</li>
                <li><strong>Partners You Invite:</strong> When using couples mode, your compatibility results are shared with invited partners</li>
                <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">5. Your Rights</h2>
              <p className="mb-3">You have the right to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Access your personal data</li>
                <li>Correct inaccurate information</li>
                <li>Request deletion of your account and data</li>
                <li>Opt-out of marketing communications</li>
                <li>Download your quiz results and data</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">6. Cookies & Tracking</h2>
              <p>
                We use cookies and similar technologies to:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-3">
                <li>Keep you logged in</li>
                <li>Remember your preferences (e.g., dark mode)</li>
                <li>Analyze site usage and improve performance</li>
              </ul>
              <p className="mt-3">
                You can control cookies through your browser settings.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">7. Data Retention</h2>
              <p>
                We retain your information for as long as your account is active or as needed to provide services. 
                You can request deletion at any time by contacting us.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">8. Children's Privacy</h2>
              <p>
                LuvCup is not intended for users under 17 years of age. We do not knowingly collect 
                information from children under 17.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">9. Changes to This Policy</h2>
              <p>
                We may update this privacy policy from time to time. We will notify you of significant 
                changes via email or a notice on our website.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-4">10. Contact Us</h2>
              <p className="mb-3">
                If you have questions about this privacy policy or your data, contact us at:
              </p>
              <div className="bg-gradient-to-r from-rose-50 to-pink-50 p-4 rounded-lg">
                <p className="font-semibold">Email: privacy@luvcup.app</p>
                <p className="font-semibold">Support: support@luvcup.app</p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
